
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { InviteMemberFormData, permissionOptions, MemberPermission } from '../types/memberTypes';
import { validateEmail, validateName, validatePhone } from '../utils/validationUtils';
import { useForm } from 'react-hook-form';

// Import refactored components
import UserInfoFields from './invitation/UserInfoFields';
import RoleSelector from './invitation/RoleSelector';
import PermissionsDropdown from './invitation/PermissionsDropdown';
import DialogFooterButtons from './invitation/DialogFooterButtons';

interface InviteMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formState: InviteMemberFormData;
  onSubmit: (data: InviteMemberFormData) => void;
  source?: 'portfolio' | 'business';
}

const InviteMemberDialog: React.FC<InviteMemberDialogProps> = ({
  open,
  onOpenChange,
  formState,
  onSubmit,
  source = 'portfolio'
}) => {
  const form = useForm({
    defaultValues: {
      firstName: formState.firstName,
      lastName: formState.lastName,
      email: formState.email,
      phone: formState.phone || '',
      role: formState.role,
      permissions: formState.permissions
    }
  });
  
  const setFormField = formState.setFormField;
  
  // Log dialog state for debugging
  useEffect(() => {
    console.log('InviteMemberDialog - open state:', open);
  }, [open]);
  
  // Effect to set all permissions when role is Owner
  useEffect(() => {
    if (formState.role === 'Owner') {
      const allPermissionsChecked = formState.permissions.map(permission => ({
        ...permission,
        checked: permission.id == "viewOnly" ? false : true
      }));
      setFormField('permissions', allPermissionsChecked);
    }
  }, [formState.role, setFormField]);
  
  const [errors, setErrors] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  }>({});
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('InviteMemberDialog - handleSubmit called');
    
    const validationErrors = {
      firstName: validateName(formState.firstName),
      lastName: validateName(formState.lastName),
      email: validateEmail(formState.email),
      phone: validatePhone(formState.phone),
    };
    
    const filteredErrors = Object.fromEntries(
      Object.entries(validationErrors).filter(([_, value]) => value)
    );
    
    setErrors(filteredErrors);
    
    if (Object.keys(filteredErrors).length === 0) {
      console.log('InviteMemberDialog - Submitting form data:', formState);
      onSubmit(formState);
    }
  };
  
  const entityName = source === 'portfolio' ? 'Portfolio' : 'Business';
  
  return (
    <Dialog 
      open={open} 
      onOpenChange={(newState) => {
        console.log(`Dialog onOpenChange called with: ${newState}`);
        onOpenChange(newState);
      }}
    >
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Invite {entityName} Member</DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 py-4">
              {/* User information fields */}
              <UserInfoFields 
                firstName={formState.firstName}
                lastName={formState.lastName}
                email={formState.email}
                phone={formState.phone}
                errors={errors}
                setFormField={setFormField}
              />
              
              {/* Role selector */}
              <RoleSelector 
                role={formState.role}
                setFormField={setFormField}
              />
              
              {/* Permissions dropdown (only for Manager role) */}
              {formState.role === 'Manager' && (
                <PermissionsDropdown 
                  permissions={formState.permissions}
                  setFormField={setFormField}
                />
              )}
              {/* Note: Owner role automatically gets all permissions */}
            </div>
            
            {/* Dialog footer buttons */}
            <DialogFooterButtons 
              onCancel={() => onOpenChange(false)}
              onSubmit={handleSubmit}
            />
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default InviteMemberDialog;
